import React, {Fragment, useState} from 'react';
import {HibuTag, HibuTags} from '../../../../../search-types';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {EditableTag} from '../../editable-tag/editableTag';
import {SubmittableInput} from '../../../../../../common/submittable-input/submittableInput';
import {SvgIcon} from '../../../../../../icons/svgIcon';
import {Icons} from '../../../../../../icons/icons';
import {buildDefaultFieldTitle, getOpenstreetmapUrl, isEmptyArray} from '../../../../../../utils/utils';
import classNames from 'clsx';
import {useStateFacets} from '../../../../../search-hooks';
import {SelectionType} from '../../../../../../facets/facetTypes';
import {useLocalizationUtil} from '../../../../../../localizationUtil';
import {HibuSearchableSelect} from '../../../../../../common/select/hibuSearchableSelect';
import createOption from '../../../../../../common/select/hibuSelectUtil';
import {Option} from '../../../../../../common/select/select';
import {renderActionButtons} from '../../../../../../common/submittable-input/submittable-util';
import {useSpotlightTagTypes} from '../../../../../../config/config-hooks';

type HibuTagListProps = {
    hibuTags: HibuTags;
    removeTag: (tagCategory: string, tag: HibuTag) => void;
    changeTag: (
        tagCategory: string,
        tag: HibuTag,
        labels: { [language: string]: string } | null,
        extra: { [key: string]: any } | null
    ) => void;
    addTag: (
        tagCategory: string,
        tagId: string | null,
        labels: { [language: string]: string } | null,
        extra: { [key: string]: any } | null
    ) => void;
};

export const HibuTagList = React.memo((props: HibuTagListProps) => {
    const {t} = useTranslation();
    const showTechnicalDetails = useSelector((state) => state.settings.technicalDetails);
    const showManualValidation = useSelector((state) => state.settings.manualValidation);
    const searchConfig = useSelector((state) => state.config.search);
    const tagWithDropdown = searchConfig.tagsWithDropdown.map(tag => tag.split('.').pop());
    const tagsWithConfirmation = searchConfig.tagsWithConfirmation.map(tag => tag.split('.').pop());
    const facets = useStateFacets();
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const spotlightTagTypeNames = useSpotlightTagTypes().map(tt => tt.name);
    const [categoryWhereEntryIsAdded, changeCategoryWhereEntryIsAddedTo] = useState<string | null>(null);
    const {formatNumber, selectedLanguage: currentLanguage} = useLocalizationUtil();

    if (!props || !props.hibuTags) {
        return null;
    }

    const extractFacetFieldNames = () => {
        const fieldNames: string[] = [];
        facets.facetGroups.allKeys
            .filter(
                (id) => facets.facetGroups.byKey[id]?.selectionType !== SelectionType.HIDDEN && id.startsWith('tags')
            )
            .forEach((facetName) => {
                const fieldName = facetName.substring(facetName.indexOf('.') + 1).replace(/_.*/, '');
                fieldNames.push(fieldName);
            });
        return fieldNames;
    };

    const extractEmptyCategories = () => {
        const emptyCategories: string[] = facetFieldNames.filter(name => !spotlightTagTypeNames.includes(name));
        Object.keys(props.hibuTags).forEach((hibuTagsKey) => {
            if (props.hibuTags[hibuTagsKey].length > 0) {
                const index = emptyCategories.indexOf(hibuTagsKey);
                emptyCategories.splice(index, 1);
            }
        });
        return emptyCategories;
    };

    const facetFieldNames = extractFacetFieldNames();
    const emptyTagCategories = extractEmptyCategories();
    const hasEmptyCategories = emptyTagCategories.length >= 1;
    const emptyCategoriesCount = emptyTagCategories.length;

    const renderTagGroupTitle = (tagTitle: string) => {
        const title = t('fieldTitle.tagsPlural.' + tagTitle + '.label', buildDefaultFieldTitle(tagTitle));
        return (
            <div className="tag-name cell">{title}</div>
        );
    };

    const formatTagLabel = (tagCategory: string, tag: HibuTag) => {
        return (
            <Fragment>
                {tag.label} {showWikidataId(tagCategory, tag)} {showLocation(tagCategory, tag)}
            </Fragment>
        );
    };

    const showWikidataId = (tagCategory: string, tag: HibuTag) => {
        if (tag.id && showTechnicalDetails && tagCategory !== 'doctype' && tag.id.startsWith('wikidata')) {
            const wikidataId = tag.id.substr(tag.id.lastIndexOf(':') + 1);
            const wikidataUrl = 'https://www.wikidata.org/wiki/' + wikidataId;
            return (
                <a href={wikidataUrl} target="_blank" rel="noopener noreferrer" className="tag-link-icon">
                    ({wikidataId})
                </a>
            );
        }
        return null;
    };

    const showLocation = (tagCategory: string, tag: HibuTag) => {
        if (showTechnicalDetails && tagCategory === 'location' && tag.extra?.coordinates && tag.extra?.area) {
            const url = getOpenstreetmapUrl(tag);
            return (
                <a href={url} target="_blank" rel="noopener noreferrer" className="tag-link-icon">
                    <SvgIcon icon={Icons.MAP}/>
                </a>
            );
        }
        return null;
    };

    const mayEdit = () => {
        return showManualValidation;
    };

    const onDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    const showEmptyCategoriesText = () => {
        return (
            <div className="empty-categories-element">
                <div className="empty-categories-placeholder" onClick={onDropdown}>
                    {showDropdown && (
                        t('search.details.closeCategories')
                    )}
                    {!showDropdown && (
                        emptyCategoriesCount > 1
                            ? t('search.details.emptyCategories', {entry: formatNumber(emptyCategoriesCount)})
                            : t('search.details.emptyCategory', {entry: formatNumber(emptyCategoriesCount)})
                    )}
                </div>
            </div>
        );
    };

    const showAddTagElements = () => {
        return emptyTagCategories.map((category) => (
            <div className="document-tag" key={category}>
                <div className="tag-name cell">{t('fieldTitle.tagsPlural.' + category + '.label')}</div>
                <div className="tag-value cell">{mayEdit() && showAddTagElement(category)}</div>
            </div>
        ));
    };

    const showAddTagElement = (category: string) => {
        const addButton = (
            <div className="add-tag-placeholder" onClick={() => changeCategoryWhereEntryIsAddedTo(category)}>
                <SvgIcon className="add-tag" icon={Icons.ADD} alt={t('search.details.add')}/>
                <span>{t('search.details.add')}</span>
            </div>
        );

        const onSave = (value: string | Option) => {
            let id: string | null;
            let label: string;
            let extra: { [key: string]: any; } | null;
            if (typeof value === 'string') {
                id = null;
                label = value;
                extra = null;
            } else {
                id = value.value;
                label = value.label;
                extra = {userValidated_bool: true};
            }
            props.addTag(category, id, {[currentLanguage]: label}, extra);
        };

        const onDelete = () => {
            changeCategoryWhereEntryIsAddedTo(null);
        };
        const addFields = mayEdit() && hasOptions(category) ?
            <>
                <HibuSearchableSelect
                    value={undefined}
                    placeholder={t('general.select', {entry: category})}
                    options={getTagOptions(category)}
                    onChange={value => onSave(value)}
                    onBlur={onDelete}
                />
                {renderActionButtons(onDelete, t)}
            </>
            :
            <SubmittableInput
                fieldType="line"
                initialValue={''}
                onSubmit={onSave}
                onCancel={() => changeCategoryWhereEntryIsAddedTo(null)}
            />;

        return (<div className="add-tag-element">
            {categoryWhereEntryIsAdded !== category ? addButton : addFields}
        </div>);
    };

    function notAggregated(tagGroupElement: HibuTag[]) {
        return tagGroupElement.find((tag) => tag.extra?.aggregated) === undefined;
    }

    function getTagOptions(tagGroupName: string): Option[] {
        return searchConfig.tagDropdownOptions && searchConfig.tagDropdownOptions[tagGroupName] ? searchConfig.tagDropdownOptions[tagGroupName] : [];
    }

    function hasOptions(tagGroupName: string): boolean {
        return tagWithDropdown.includes(tagGroupName);
    }

    const isTagWithConfirmation = (tagGroupName: string): boolean => {
        return tagsWithConfirmation.includes(tagGroupName);
    };

    const showHibuTags = (tagGroupName: string, tagGroupValues: HibuTag[], tagIndex: number) => {
        if (tagGroupValues &&
            tagGroupValues.length > 0 &&
            notAggregated(tagGroupValues) &&
            tagGroupName !== 'heading' &&
            tagGroupName !== 'sectionType'
        ) {
            const tagOptions = getTagOptions(tagGroupName);

            const canBeConfirmed = isTagWithConfirmation(tagGroupName);
            return (
                <div key={'id_' + tagIndex} className="document-tag">
                    {renderTagGroupTitle(tagGroupName)}
                    <div className="tag-value cell">
                        {tagGroupValues?.map((tag: HibuTag, i: number) => {
                            const onChange = (value: string | Option) => {
                                let newTag: HibuTag;
                                let label: string;
                                const extra: { [key: string]: any; } = {userValidated_bool: true};
                                if (typeof value === 'string') {
                                    label = value;
                                    newTag = tag;
                                } else {
                                    // dropdown case
                                    newTag = {...tag, id: value.value};
                                    label = value.label;
                                }
                                props.changeTag(tagGroupName, newTag, {[currentLanguage]: label}, extra);
                            };
                            const toggleValidated = () => {
                                const isValidatedNew = tag.extra?.userValidated_bool ? !tag.extra?.userValidated_bool : true;
                                const extra: { [key: string]: any; } = {
                                    ...tag.extra,
                                    userValidated_bool: isValidatedNew
                                };
                                props.changeTag(tagGroupName, tag, {[currentLanguage]: tag.label}, extra);
                            };


                            const validated = tag.extra?.['userValidated_bool'];
                            const onConfirm = canBeConfirmed ? () => toggleValidated() : undefined;
                            if (mayEdit() && hasOptions(tagGroupName)) {
                                const buttons = renderActionButtons(() => props.removeTag(tagGroupName, tag), t, validated, onConfirm);
                                return (<div className="tag-value_dropdown-wrapper">
                                    <HibuSearchableSelect
                                        name={`${i}`}
                                        className="tag-value-select"
                                        value={createOption(tag.id, tag.label)}
                                        options={tagOptions}
                                        onChange={onChange}
                                    />
                                    {buttons}
                                </div>);
                            }
                            return <EditableTag
                                fieldType="line"
                                key={i}
                                content={formatTagLabel(tagGroupName, tag)}
                                editableLabel={tag.label}
                                allowEditing={mayEdit()}
                                allowRemoval={mayEdit()}
                                onTagRemoval={() => props.removeTag(tagGroupName, tag)}
                                onTagChange={onChange}
                                validated={validated}
                                onTagValidation={onConfirm}
                            />;
                        })}
                        {mayEdit() && showAddTagElement(tagGroupName)}
                    </div>
                </div>
            );
        }
    };

    const showTagTypesFromConfig = searchConfig.detailFields?.detailTagTypes?.map((tagTypeName: string, tagIndex) => {
        const tagGroupValues = props.hibuTags[tagTypeName];
        return (
            <>
                {showHibuTags(tagTypeName, tagGroupValues, tagIndex)}
            </>
        );
    });

    const showTagTypesAll = Object.entries(props.hibuTags).map((tagGroup, tagIndex) => {
        const tagGroupValues = tagGroup[1];
        return (
            <>
                {showHibuTags(tagGroup[0], tagGroupValues, tagIndex)}
            </>
        );
    });

    return (
        <div className={classNames('document-infos', 'hibu-tags')}>
            {isEmptyArray(searchConfig.detailFields?.detailTagTypes) ? showTagTypesAll : showTagTypesFromConfig}
            {hasEmptyCategories && showEmptyCategoriesText()}
            {showDropdown && showAddTagElements()}
        </div>
    );
});
